import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Lock, Eye, EyeOff, ShieldCheck, AlertTriangle } from "lucide-react";
import api, { apiError } from "@/lib/api";

export default function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      toast.error("Les mots de passe ne correspondent pas.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, password });
      toast.success(data.message || "Mot de passe réinitialisé !");
      navigate("/connexion", { replace: true });
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Échec de la réinitialisation");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F6F0] flex flex-col">
      <div className="relative h-44 overflow-hidden bg-[#1B4332]">
        <div className="absolute inset-0 fabric-grain opacity-20" />
        <div className="absolute bottom-4 left-5">
          <h1 className="font-display text-2xl font-extrabold text-white">SuccèsNet</h1>
          <p className="text-white/80 text-sm">Nouveau mot de passe</p>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex-1 -mt-6 bg-[#F9F6F0] rounded-t-3xl px-5 pt-7 max-w-lg w-full mx-auto"
      >
        {!token ? (
          <div className="text-center py-8" data-testid="reset-no-token">
            <div className="h-16 w-16 rounded-full bg-[#E85D04]/10 flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="h-8 w-8 text-[#E85D04]" />
            </div>
            <h2 className="font-display text-xl font-extrabold mb-2">Lien invalide</h2>
            <p className="text-sm text-[#5C574F] mb-6">Ce lien de réinitialisation est incomplet ou a expiré.</p>
            <button
              data-testid="request-new-link-btn"
              onClick={() => navigate("/mot-de-passe-oublie")}
              className="w-full h-14 rounded-2xl bg-[#E85D04] text-white font-bold active:scale-95 transition-transform"
            >
              Demander un nouveau lien
            </button>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2 text-[#2D6A4F] text-sm font-semibold mb-5">
              <ShieldCheck className="h-5 w-5" /> Choisissez un nouveau mot de passe sécurisé
            </div>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-[#5C574F] mb-1.5">Nouveau mot de passe</label>
                <div className="relative">
                  <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-5 w-5 text-[#9c968a]" />
                  <input
                    data-testid="new-password-field"
                    type={showPwd ? "text" : "password"}
                    required
                    minLength={6}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full h-14 bg-[#F9F6F0] rounded-xl pl-11 pr-12 outline-none border border-transparent focus:border-[#E85D04] focus:ring-1 focus:ring-[#E85D04]/20"
                  />
                  <button
                    type="button"
                    data-testid="toggle-new-password-btn"
                    onClick={() => setShowPwd((v) => !v)}
                    aria-label={showPwd ? "Masquer" : "Afficher"}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-[#9c968a] hover:text-[#5C574F] transition-colors"
                  >
                    {showPwd ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#5C574F] mb-1.5">Confirmer le mot de passe</label>
                <div className="relative">
                  <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-5 w-5 text-[#9c968a]" />
                  <input
                    data-testid="confirm-password-field"
                    type={showPwd ? "text" : "password"}
                    required
                    minLength={6}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    placeholder="••••••••"
                    className="w-full h-14 bg-[#F9F6F0] rounded-xl pl-11 pr-4 outline-none border border-transparent focus:border-[#E85D04] focus:ring-1 focus:ring-[#E85D04]/20"
                  />
                </div>
              </div>
              <button
                data-testid="reset-submit-btn"
                type="submit"
                disabled={busy}
                className="w-full h-14 rounded-2xl bg-[#E85D04] text-white font-bold active:scale-95 transition-transform disabled:opacity-60 shadow-lg shadow-[#E85D04]/20"
              >
                {busy ? "Réinitialisation…" : "Réinitialiser mon mot de passe"}
              </button>
            </form>
          </>
        )}
      </motion.div>
    </div>
  );
}
